import React, { useState } from "react";
import { api } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader } from "lucide-react";

export function CreateJobDialog({ open, onOpenChange, onCreated }) {
  const [loading, setLoading] = useState(false);
  const [lookupType, setLookupType] = useState("ip");
  const [formData, setFormData] = useState({
    ip: "",
    domain: "",
    blacklist: "",
    blacklist_name: "",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const target = lookupType === "domain" ? formData.domain : formData.ip;
    if (!target || !formData.blacklist) return;

    setLoading(true);
    try {
      const result = await api.createJob({
        ip: lookupType === "ip" ? formData.ip : undefined,
        domain: lookupType === "domain" ? formData.domain : undefined,
        blacklist: formData.blacklist,
        blacklist_name: formData.blacklist_name || formData.blacklist,
        context: {},
      });
      onCreated?.(result);
      onOpenChange(false);
      setFormData({
        ip: "",
        domain: "",
        blacklist: "",
        blacklist_name: "",
      });
      setLookupType("ip");
    } catch (err) {
      console.error("Create job failed:", err);
    } finally {
      setLoading(false);
    }
  };

  const targetValue = lookupType === "domain" ? formData.domain : formData.ip;
  const setTargetValue = (v) => {
    if (lookupType === "domain") {
      setFormData((p) => ({ ...p, domain: v }));
    } else {
      setFormData((p) => ({ ...p, ip: v }));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>New Delisting Job</DialogTitle>
          <DialogDescription>
            Start an autonomous delisting process for a blacklisted IP or
            domain.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="flex items-center gap-4">
            <Label className="text-sm font-medium">Lookup Type</Label>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input
                  type="radio"
                  name="lookupType"
                  value="ip"
                  checked={lookupType === "ip"}
                  onChange={() => setLookupType("ip")}
                  className="text-primary"
                />
                <span className="text-sm">IP</span>
              </label>
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input
                  type="radio"
                  name="lookupType"
                  value="domain"
                  checked={lookupType === "domain"}
                  onChange={() => setLookupType("domain")}
                  className="text-primary"
                />
                <span className="text-sm">Domain</span>
              </label>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="target">
                {lookupType === "domain" ? "Domain Name *" : "IP Address *"}
              </Label>
              <Input
                id="target"
                data-testid={
                  lookupType === "domain" ? "input-domain" : "input-ip"
                }
                placeholder={
                  lookupType === "domain" ? "example.com" : "5.180.194.20"
                }
                value={targetValue}
                onChange={(e) => setTargetValue(e.target.value)}
                className="font-mono"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="blacklist">Blacklist Host *</Label>
              <Input
                id="blacklist"
                data-testid="input-blacklist"
                placeholder="zen.spamhaus.org"
                value={formData.blacklist}
                onChange={(e) =>
                  setFormData((p) => ({ ...p, blacklist: e.target.value }))
                }
                className="font-mono"
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="blacklist_name">Blacklist Name</Label>
            <Input
              id="blacklist_name"
              data-testid="input-blacklist-name"
              placeholder="Spamhaus ZEN"
              value={formData.blacklist_name}
              onChange={(e) =>
                setFormData((p) => ({ ...p, blacklist_name: e.target.value }))
              }
            />
          </div>

          <DialogFooter className="relative z-50">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              data-testid="btn-cancel-create"
              style={{ position: "relative", zIndex: 60 }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading || !targetValue || !formData.blacklist}
              data-testid="btn-submit-job"
            >
              {loading ? (
                <>
                  <Loader className="w-4 h-4 mr-2 animate-spin" /> Starting...
                </>
              ) : (
                "Start Delisting"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
